import React, { useRef, useEffect } from 'react'

import { useForTablet, DocumentHead, ErrorMessage } from '@sketch/components'

// Constants
import { localStorageKeys } from '@sketch/constants'
import { useFlag } from 'containers/flags'

// Utils
import { getParsedItem, setStringifiedItem, useOnEvent } from '@sketch/utils'

// Hooks
import useUserProfile from 'modules/user/useUserProfile'
import { useBanner } from 'containers/Banner'
import { useCanUseDesignSystemManager } from 'modules/designSystems/operations/useCanUseDesignSystemManager'

// Components
import DocumentHeader from 'components/DocumentHeader'
import MfaBanner from 'components/MfaBanner'
import { handleFetchMore } from 'components/InfiniteList'
import { DocumentItemDropdown } from 'modules/shares/components/DocumentItemDropdown'

// Local Components
import DocumentItemsLayout from './DocumentItemsLayout'
import SharesEmptyState from './ShareEmptyState'

// Types
import {
  useGetSharesQuery,
  useGetSharesLibrariesQuery,
  useGetWorkspaceUploadsRestrictedForQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { ErrorHandler } from '@sketch/tracing'

interface WorkspaceSharesListProps {
  workspaceId: string
  workspace?: WorkspaceMinimalFragment
  search?: string
  filter?: 'LIBRARY' | 'NO_PROJECT' | 'SHARED_WITH_ME' | 'TEMPLATE' | null
  isMember?: boolean
}

// Helpers
const entriesPath = ['workspace', 'shares', 'entries']

const HEADER_COPY = {
  SHARED_WITH_ME: {
    title: 'Shared with Me',
    description: 'All documents that are directly shared with you.',
  },
  LIBRARY: {
    title: 'Libraries',
    description:
      "Libraries are documents with components that can be shared across other documents. To add a Library, select Use as Library in a document's settings.",
  },
  TEMPLATE: {
    title: 'Templates',
    description:
      'Templates are reusable Workspace documents with pre-populated content.',
  },
  NO_PROJECT: null,
}

const WorkspaceSharesList = ({
  workspaceId,
  search,
  filter = null,
  workspace,
  isMember,
}: WorkspaceSharesListProps) => {
  const isDesignSystemsOn = useFlag('design-systems')
  const isTabletAndBigger = useForTablet()
  const workspaceHasDocuments = useRef<boolean>()
  const { showBanner } = useBanner()

  const userProfile = useUserProfile()
  const isVerified = userProfile?.data?.me?.isVerified
  const isMfaEnabled = userProfile?.data?.me?.mfaState === 'ENABLED'
  const isSsoWorkspace = workspace?.customer?.ssoEnabled

  const { canUseDesignSystemManager } = useCanUseDesignSystemManager({
    workspace,
  })

  const variables = {
    identifier: workspaceId,
    after: null,
    search: {
      name: search,
      filter,
    },
  }

  // check if the user has permissions to get the design systems - DIFFERENT from the business flag
  const canAccessDesignSystems =
    isMember && isDesignSystemsOn && filter === 'LIBRARY'

  const resultDocuments = useGetSharesQuery({
    variables,
    fetchPolicy: 'cache-and-network',
    skip: canAccessDesignSystems,
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.identifier !== curr?.identifier,
  })

  const resultLibrary = useGetSharesLibrariesQuery({
    variables,
    fetchPolicy: 'cache-and-network',
    skip: !canAccessDesignSystems,
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.identifier !== curr?.identifier,
  })

  // libraries need to get the design systems data
  const result = canAccessDesignSystems ? resultLibrary : resultDocuments

  const uploadsRestrictedForResult = useGetWorkspaceUploadsRestrictedForQuery({
    variables: { identifier: workspaceId },
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.identifier !== curr?.identifier,
  })

  useOnEvent('workspaceShareRefresh', ({ workspaceIdentifier }) => {
    if (workspaceIdentifier !== workspaceId) {
      // Not the workspace visible ignore then
      return
    }
    result.refetch()
  })

  // We are keeping the totalCount in a ref
  // to see if the Workspace has documents
  // to control Banner logic even if a search
  // returns a totalCount of 0
  useEffect(() => {
    if (!search) {
      const totalCount = result?.data?.workspace?.shares?.meta?.totalCount
      workspaceHasDocuments.current = !!totalCount
    }
  }, [result, search])

  const sharesCount = result.data?.workspace.shares.meta.totalCount ?? 0

  // Mfa Banner to encourage the user to enable it
  useEffect(() => {
    if (!isMfaEnabled && isVerified && sharesCount !== 0 && !isSsoWorkspace) {
      const now = new Date()
      const stopRenderAfter = getParsedItem(localStorageKeys.mfaBanner)

      // if the date wasn't set before, set it
      if (!stopRenderAfter) {
        const oneDayFromNow = new Date(Date.now() + 3600 * 1000 * 24)

        setStringifiedItem(localStorageKeys.mfaBanner, oneDayFromNow)
      }

      if (!(now > new Date(stopRenderAfter))) {
        const { dismissBanner } = showBanner({
          id: 'mfa-banner',
          type: 'custom',
          message: <MfaBanner />,
        })

        return () => {
          dismissBanner('mfa-banner')
        }
      }
    }
  }, [isSsoWorkspace, isMfaEnabled, isVerified, sharesCount, showBanner])

  if (!result?.data && result?.error) {
    return <ErrorMessage.Generic />
  }

  const shares = result?.data?.workspace.shares.entries || []

  const after = result.data?.workspace.shares.meta.after || null
  const loadMoreHandler = handleFetchMore(result.fetchMore, entriesPath, {
    after,
  })

  const loading = result.networkStatus < 3 || uploadsRestrictedForResult.loading
  const totalShareCount = result?.data?.workspace.shares.meta.totalCount || 0

  // if we are on libraries, we don't want to show an empty screen straight away
  if (!loading && shares.length === 0) {
    const uploadsRestrictedFor =
      uploadsRestrictedForResult.data?.workspace?.uploadsRestrictedFor ??
      // we should never need to default back to 'NONE', as we just checked
      // that uploadsRestrictedForResult is not in a loading state anymore
      // So there should be a valid data, but just in case, default it back
      // to 'NONE' either way.
      'NONE'

    return (
      <SharesEmptyState
        filter={filter}
        search={search}
        workspace={workspace!}
        workspaceHasDocuments={workspaceHasDocuments.current}
        uploadsRestrictedFor={uploadsRestrictedFor}
      />
    )
  }

  const headerCopy = filter && HEADER_COPY[filter]

  return (
    <>
      {headerCopy && <DocumentHead title={headerCopy.title} />}
      <DocumentItemsLayout
        loading={loading}
        shares={shares}
        search={search}
        renderDropdown={({ identifier }) => {
          const share = shares.find(x => x.identifier === identifier)
          if (!share) {
            ErrorHandler.shouldNeverHappen(
              'share should always be present rendering the DocumentDropdown'
            )
            return null
          }
          return (
            <DocumentItemDropdown
              share={share}
              workspaceIdentifier={workspaceId}
            />
          )
        }}
        onLoadMore={loadMoreHandler}
        totalShareCount={totalShareCount}
        canUseDesignSystemManager={canUseDesignSystemManager}
        header={
          isTabletAndBigger &&
          headerCopy && (
            <DocumentHeader
              title={headerCopy.title}
              description={headerCopy.description}
            />
          )
        }
        showProjectName
      />
    </>
  )
}

export default WorkspaceSharesList
